import React, { useState, useEffect } from 'react';
import { verifyToken, callAPI } from '../utils';
import { Box, CircularProgress, Stack } from '@mui/material';
import OtpInput from '../ui/otpInput';
import Button from '../ui/button';
import Alert from '../ui/alert';

const TwoFactorAuth = ({
  onSuccess,
  onVerify = null,
  email = 'default@default.com',
  password = 'password',
  buttonBgColor = '#FDA041',
}) => {
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [checkingOTPStatus, setCheckingOTPStatus] = useState(true);

  useEffect(() => {
    const checkOtpStatus = async () => {
      setCheckingOTPStatus(true);
      try {
        const response = await verifyToken();
        const isOtpEnabled = response?.data.userDetails.authenticator ?? true;

        if (onVerify && typeof onVerify === 'function') {
          const verifiedOtpStatus = await onVerify(isOtpEnabled);
          setOtpEnabled(verifiedOtpStatus ?? isOtpEnabled);
        } else {
          setOtpEnabled(isOtpEnabled);
        }

        if (!isOtpEnabled) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error checking OTP status:', error);
        setErrorMessage('Failed to check OTP status.');
        setOtpEnabled(false);
      } finally {
        setCheckingOTPStatus(false);
      }
    };

    checkOtpStatus();
  }, [onVerify]);

  const enteredOtp = otp.join('');

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await callAPI({
        request: 'verifyOtp',
        method: 'post',
        params: {
          email,
          otp: enteredOtp,
        },
        onSuccess: (response) => {
          onSuccess(response?.data?.accessToken);
        },
        onError: () => {
          setErrorMessage('Invalid OTP. Please try again.');
        },
      });
    } catch (error) {
      setErrorMessage('An error occurred while verifying OTP.');
      console.error('OTP Verification Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (checkingOTPStatus) {
    return;
  }

  return (
    <Box>
      {otpEnabled ? (
        <form onSubmit={handleOtpSubmit}>
          <Stack spacing="1.5rem">
            <Box sx={{ pb: 0.5 }}>
              <OtpInput value={otp} onChange={(newArray) => setOtp(newArray)} />
            </Box>
            {errorMessage && (
              <Alert
                type="error"
                message={errorMessage}
                onClose={() => setErrorMessage('')}
              />
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              bgColor={buttonBgColor}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Verify OTP'
              )}
            </Button>
          </Stack>
        </form>
      ) : (
        <Alert
          type="error"
          message="OTP not enabled. Please check your settings."
        />
      )}
    </Box>
  );
};

export default TwoFactorAuth;
